/**
 * 设备协议升级 SDK。
 *
 * 当前实现的升级请求（type = 1）包结构：
 * - byte0: bit0-4 command，bit5-6 deviceType，bit7 保留为 0
 * - byte1: 包索引，单包请求固定为 0
 * - byte2: bit7=末包标志，bit0-6=从 byte3 开始的数据长度
 * - byte3: type，升级请求固定为 1
 * - byte4-7: productId，小端序
 * - byte8-62: 保留为 0
 * - byte63: 校验和，由 HIDSender.sendOutputReport 写入
 */
class ProtocolUpgradeSDK {
  /** HID 协议包固定长度；不包含 WebHID API 单独传入的 reportId。 */
  static REPORT_SIZE = 64;

  /** 当前阶段使用的默认设备类型，写入 byte0 的 bit5-6。 */
  static DEVICE_TYPE_DEFAULT = 0;

  /** 查询设备升级信息，并取得 MAC、APP 版本和当前镜像号。 */
  static TYPE_UPGRADE_REQUEST = 1;

  /** 将设备返回的 MAC 与 BIN 文件中的 AES Key 发回设备校验。 */
  static TYPE_VERIFICATION_INFO_REQUEST = 2;

  /**
   * APP 升级请求及其响应共用 type=6。
   * 请求携带目标镜像参数，响应携带 appRNG[5]。
   */
  static TYPE_APP_UPGRADE = 6;

  /** 使用 appRNG 计算 CheckData 后发回设备的请求。 */
  static TYPE_APP_CHECK_REQUEST = 7;

  /** 固件数据长包使用的固定 command（byte0 bit0-4）。 */
  static FIRMWARE_DATA_COMMAND = 30;

  /** type=1/2/6/7 升级控制握手使用的 command。 */
  static FIRMWARE_CONTROL_COMMAND = 29;

  /** 固件长包 byte4-62 可承载的最大数据长度。 */
  static FIRMWARE_DATA_SIZE = 59;

  /** 等待 command=30 分包 ACK 的默认超时时间。 */
  static FIRMWARE_ACK_TIMEOUT = 100;

  /** 单个分包在首次发送失败后允许的默认重发次数。 */
  static FIRMWARE_MAX_RETRIES = 3;

  /**
   * 创建一个绑定到指定 HID 发送器的升级 SDK。
   *
   * 构造函数只保存发送器，不会打开设备或发送数据。绑定一次后，后续
   * type=1、type=2 以及固件分包流程可复用同一个发送通道。
   *
   * @param {import('./HIDSender').default} sender
   *   当前已选设备对应的 HIDSender。必须实现 sendOutputReport；
   *   等待 type=1、type=2、type=6 返回时还要求它实现 waitForResponse。
   * @throws {TypeError} sender 不存在或不具备发送能力时抛出
   */
  constructor(sender) {
    if (!sender || typeof sender.sendOutputReport !== 'function') {
      throw new TypeError('sender 必须提供 sendOutputReport(reportId, data) 方法');
    }
    this.sender = sender;
    /**
     * 最近一次 type=6 返回的 appRNG[5]。
     *
     * 开始新升级会话时清空；收到 type=6 后保存独立副本，供 type=7 或
     * 后续协议步骤复用。尚未收到 type=6 时为 null。
     * @type {number[]|null}
     */
    this.appRNG = null;
  }

  /**
   * 组装协议 byte0。
   *
   * 此方法只负责位字段组合，不创建完整数据包：
   * bit0-4 保存 command，bit5-6 保存 deviceType，bit7 保持为 0。
   * 先校验再组合，避免过大的数值覆盖相邻位。
   *
   * @param {number} command 5-bit 命令号，范围 0-31
   * @param {number} deviceType 2-bit 设备类型，范围 0-3
   * @returns {number} 可直接写入协议 byte0 的单字节数值
   * @throws {RangeError} command 或 deviceType 不是有效整数时抛出
   */
  static buildCommandByte(command, deviceType = ProtocolUpgradeSDK.DEVICE_TYPE_DEFAULT) {
    ProtocolUpgradeSDK.assertIntegerInRange(command, 0, 0x1f, 'command');
    ProtocolUpgradeSDK.assertIntegerInRange(deviceType, 0, 0x03, 'deviceType');
    return command | (deviceType << 5);
  }

  /**
   * 创建 type=1 的升级请求包。
   *
   * 该方法只构造数据，不与设备通信。productId 按小端序写入 byte4-7。
   * byte63 此时仍为 0，实际发送时由 HIDSender 写入包校验和。
   *
   * @param {{command:number, productId:number, deviceType?:number}} options
   * @param {number} options.command 升级协议使用的 5-bit 命令号
   * @param {number} options.productId 目标产品 ID，范围 0-0xFFFFFFFF
   * @param {number} [options.deviceType=0] 设备类型，范围 0-3
   * @returns {Uint8Array} 新创建的 64 字节请求包
   * @throws {RangeError} 任一数值字段超出协议范围时抛出
   */
  static buildUpgradeRequest({
    command,
    productId,
    deviceType = ProtocolUpgradeSDK.DEVICE_TYPE_DEFAULT,
  }) {
    ProtocolUpgradeSDK.assertIntegerInRange(productId, 0, 0xffffffff, 'productId');

    const packet = new Uint8Array(ProtocolUpgradeSDK.REPORT_SIZE);
    packet[0] = ProtocolUpgradeSDK.buildCommandByte(command, deviceType);
    packet[1] = 0;
    packet[2] = 0x80 | 5;
    packet[3] = ProtocolUpgradeSDK.TYPE_UPGRADE_REQUEST;

    const view = new DataView(packet.buffer);
    view.setUint32(4, productId, true);

    return packet;
  }

  /**
   * 解析 type=1 的升级请求返回包。
   *
   * 此方法把设备原始字节转换为后续升级步骤需要的结构：
   * - MAC 供 type=2 校验信息请求使用；
   * - currentImage/inactiveImage 供后续选择升级目标镜像；
   * - appVersion 供升级版本判断。
   *
   * APP 版本按小端 UInt16 读取。单镜像模式下 inactiveImage 为 null。
   * 返回的 raw 是独立副本，不会受 WebHID 底层缓冲复用影响。
   *
   * @param {Uint8Array|ArrayBuffer} data 设备返回的原始报告
   * @returns {{
   *   raw: Uint8Array,
   *   command: number,
   *   deviceType: number,
   *   type: number,
   *   dualImageMode: boolean,
   *   dualImageModeRaw: number,
   *   mac: number[],
   *   appVersion: number,
   *   appVersionBytes: number[],
   *   currentImage: number,
   *   inactiveImage: number|null
   * }}
   * @throws {RangeError} 报告过短或镜像号不是 0/1 时抛出
   * @throws {Error} byte3 不是 type=1 时抛出
   */
  static parseUpgradeRequestResponse(data) {
    const response =
      data instanceof Uint8Array ? data.slice() : new Uint8Array(data || []);

    if (response.length < 19) {
      throw new RangeError('type=1 返回包长度不能小于 19 字节');
    }
    if (response[3] !== ProtocolUpgradeSDK.TYPE_UPGRADE_REQUEST) {
      throw new Error(
        `返回包 type 错误：期望 1，实际为 ${response[3]}`
      );
    }

    const currentImage = response[18];
    if (currentImage !== 0 && currentImage !== 1) {
      throw new RangeError(
        `当前镜像号必须是 0(IMG1) 或 1(IMG2)，实际为 ${currentImage}`
      );
    }

    const dualImageModeRaw = response[4];
    const dualImageMode = dualImageModeRaw !== 0;
    const mac = Array.from(response.slice(5, 10));
    const view = new DataView(
      response.buffer,
      response.byteOffset,
      response.byteLength
    );
    const appVersion = view.getUint16(16, true);
    const inactiveImage = dualImageMode ? (currentImage === 0 ? 1 : 0) : null;

    return {
      raw: response,
      command: response[0] & 0x1f,
      deviceType: (response[0] >> 5) & 0x03,
      type: response[3],
      dualImageMode,
      dualImageModeRaw,
      mac,
      appVersion,
      appVersionBytes: Array.from(response.slice(16, 18)),
      currentImage,
      inactiveImage,
    };
  }

  /**
   * 解析设备对 type=2 校验信息请求的响应。
   *
   * byte4 是设备校验状态：0 表示成功，非 0 表示设备拒绝校验。
   * 此方法只解析状态，不会自动发送下一条请求。
   *
   * @param {Uint8Array|ArrayBuffer} data 设备返回的原始报告
   * @returns {{
   *   raw:Uint8Array,
   *   command:number,
   *   deviceType:number,
   *   type:number,
   *   status:number,
   *   success:boolean
   * }}
   * @throws {RangeError} 返回包不足 5 字节时抛出
   * @throws {Error} byte3 不是 type=2 时抛出
   */
  static parseVerificationInfoResponse(data) {
    const response =
      data instanceof Uint8Array ? data.slice() : new Uint8Array(data || []);

    if (response.length < 5) {
      throw new RangeError('type=2 返回包长度不能小于 5 字节');
    }
    if (response[3] !== ProtocolUpgradeSDK.TYPE_VERIFICATION_INFO_REQUEST) {
      throw new Error(
        `返回包 type 错误：期望 2，实际为 ${response[3]}`
      );
    }

    const status = response[4];
    return {
      raw: response,
      command: response[0] & 0x1f,
      deviceType: (response[0] >> 5) & 0x03,
      type: response[3],
      status,
      success: status === 0,
    };
  }

  /**
   * 解析设备对 type=6 APP 升级请求返回的 type=6 数据包。
   *
   * byte4-8 是设备生成的 appRNG[5]。返回值中的 appRNG 是独立数组，
   * 调用方需要保留它，下一步构造 type=7 请求时会参与 CheckData 计算。
   *
   * @param {Uint8Array|ArrayBuffer} data 设备返回的原始报告
   * @returns {{
   *   raw:Uint8Array,
   *   command:number,
   *   deviceType:number,
   *   type:number,
   *   appRNG:number[]
   * }}
   * @throws {RangeError} 返回包不足 9 字节时抛出
   * @throws {Error} byte3 不是 type=6 时抛出
   */
  static parseAppUpgradeResponse(data) {
    const response =
      data instanceof Uint8Array ? data.slice() : new Uint8Array(data || []);

    if (response.length < 9) {
      throw new RangeError('type=6 返回包长度不能小于 9 字节');
    }
    if (response[3] !== ProtocolUpgradeSDK.TYPE_APP_UPGRADE) {
      throw new Error(
        `返回包 type 错误：期望 6，实际为 ${response[3]}`
      );
    }

    return {
      raw: response,
      command: response[0] & 0x1f,
      deviceType: (response[0] >> 5) & 0x03,
      type: response[3],
      appRNG: Array.from(response.slice(4, 9)),
    };
  }

  /**
   * 解析设备对 type=7 APP 校验请求的返回包。
   *
   * byte4 是状态：0 表示成功，可以开始发送 command=30 固件数据；
   * 4 表示失败，必须停止升级。其他非 0 状态同样按失败处理并保留原值。
   *
   * @param {Uint8Array|ArrayBuffer} data 设备返回的原始报告
   * @returns {{
   *   raw:Uint8Array,
   *   command:number,
   *   deviceType:number,
   *   type:number,
   *   status:number,
   *   success:boolean,
   *   failed:boolean
   * }}
   * @throws {RangeError} 返回包不足 5 字节时抛出
   * @throws {Error} byte3 不是 type=7 时抛出
   */
  static parseAppCheckResponse(data) {
    const response =
      data instanceof Uint8Array ? data.slice() : new Uint8Array(data || []);

    if (response.length < 5) {
      throw new RangeError('type=7 返回包长度不能小于 5 字节');
    }
    if (response[3] !== ProtocolUpgradeSDK.TYPE_APP_CHECK_REQUEST) {
      throw new Error(
        `返回包 type 错误：期望 7，实际为 ${response[3]}`
      );
    }

    const status = response[4];
    return {
      raw: response,
      command: response[0] & 0x1f,
      deviceType: (response[0] >> 5) & 0x03,
      type: response[3],
      status,
      success: status === 0,
      failed: status === 4,
    };
  }

  /**
   * 根据设备返回的 appRNG[5] 计算 type=7 使用的 CheckData[4]。
   *
   * 加法和异或完成后，每一项都执行 & 0xFF，保证结果是单字节。
   * 输入会先经过 validateByteArray，防止错误值被位运算静默截断。
   *
   * @param {ArrayLike<number>} appRNG type=6 返回的 5 字节随机数据
   * @returns {Uint8Array} 长度固定为 4 的 CheckData
   * @throws {RangeError} appRNG 长度错误或包含非字节数值时抛出
   */
  static calculateCheckData(appRNG) {
    const rng = ProtocolUpgradeSDK.validateByteArray(appRNG, 5, 'appRNG');
    return Uint8Array.from([
      ((rng[0] + rng[1]) ^ rng[4]) & 0xff,
      ((rng[1] + rng[2]) ^ rng[3]) & 0xff,
      ((rng[4] + rng[3]) ^ rng[1]) & 0xff,
      ((rng[3] + rng[4]) ^ rng[0]) & 0xff,
    ]);
  }

  /**
   * 创建 type=2 的校验信息请求包。
   *
   * 该方法只创建包，不发送数据。MAC 应直接来自
   * parseUpgradeRequestResponse 的结果，aesKeys 应直接来自
   * FirmwareBinParser.parse/parseFile 的 header。
   *
   * - byte4-8: type=1 返回包中的 MAC[5]
   * - byte9-15: BIN 文件头中的 aesKeys[7]
   * - byte16: 保留为 0
   *
   * @param {{
   *   command:number,
   *   mac:ArrayLike<number>,
   *   aesKeys:ArrayLike<number>,
   *   deviceType?:number
   * }} options
   * @param {number} options.command 升级协议使用的 5-bit 命令号
   * @param {ArrayLike<number>} options.mac type=1 返回的 5 字节 MAC
   * @param {ArrayLike<number>} options.aesKeys BIN 文件头中的 7 字节检验数据
   * @param {number} [options.deviceType=0] 设备类型，范围 0-3
   * @returns {Uint8Array} 新创建的 64 字节请求包
   * @throws {RangeError} MAC/AES 长度不对、字节越界或位字段越界时抛出
   */
  static buildVerificationInfoRequest({
    command,
    mac,
    aesKeys,
    deviceType = ProtocolUpgradeSDK.DEVICE_TYPE_DEFAULT,
  }) {
    const macBytes = ProtocolUpgradeSDK.validateByteArray(mac, 5, 'mac');
    const aesKeyBytes = ProtocolUpgradeSDK.validateByteArray(
      aesKeys,
      7,
      'aesKeys'
    );

    const packet = new Uint8Array(ProtocolUpgradeSDK.REPORT_SIZE);
    packet[0] = ProtocolUpgradeSDK.buildCommandByte(command, deviceType);
    packet[1] = 0;
    // type(1) + MAC(5) + AES Key(7) = 13 字节。
    packet[2] = 0x80 | 13;
    packet[3] = ProtocolUpgradeSDK.TYPE_VERIFICATION_INFO_REQUEST;
    packet.set(macBytes, 4);
    packet.set(aesKeyBytes, 9);

    return packet;
  }

  /**
   * 使用 type=1 返回结果和 BIN 解析结果发送 type=2 校验信息请求。
   *
   * 此方法负责从两个解析结果中提取数据，调用
   * buildVerificationInfoRequest 构造包，通过 HIDSender 发送，然后等待
   * 并解析设备的 type=2 返回包。它不会自行发送 type=6 请求；需要完整
   * 自动流程时使用 sendVerificationAndStartAppUpgrade。
   *
   * @param {{
   *   command:number,
   *   upgradeResponse:{mac:ArrayLike<number>},
   *   firmware:{header:{aesKeys:ArrayLike<number>}},
   *   deviceType?:number,
   *   reportId?:number,
   *   timeout?:number
   * }} options
   * @param {number} options.command 升级协议使用的 5-bit 命令号
   * @param {{mac:ArrayLike<number>}} options.upgradeResponse
   *   sendUpgradeRequest 返回的 type=1 解析结果
   * @param {{header:{aesKeys:ArrayLike<number>}}} options.firmware
   *   FirmwareBinParser.parse/parseFile 返回的 BIN 解析结果
   * @param {number} [options.deviceType=0] 设备类型
   * @param {number} [options.reportId=0] WebHID reportId；通常保持 0
   * @param {number} [options.timeout=1000] 等待 type=2 返回包的毫秒数
   * @returns {Promise<ReturnType<typeof ProtocolUpgradeSDK.parseVerificationInfoResponse>>}
   *   设备返回的校验状态；success 仅在 byte4 为 0 时为 true
   * @throws {Error} 输入错误、发送失败、响应超时或返回包格式错误时抛出
   */
  async sendVerificationInfoRequest({
    command,
    upgradeResponse,
    firmware,
    deviceType = ProtocolUpgradeSDK.DEVICE_TYPE_DEFAULT,
    reportId = 0,
    timeout = 1000,
  }) {
    ProtocolUpgradeSDK.assertIntegerInRange(reportId, 0, 0xff, 'reportId');
    ProtocolUpgradeSDK.assertIntegerInRange(timeout, 1, 60000, 'timeout');
    if (!upgradeResponse) {
      throw new TypeError('upgradeResponse 不能为空');
    }
    if (!firmware?.header) {
      throw new TypeError('firmware 必须是 FirmwareBinParser 的解析结果');
    }
    if (typeof this.sender.waitForResponse !== 'function') {
      throw new TypeError('sender 必须提供 waitForResponse(command, timeout) 方法');
    }

    const packet = ProtocolUpgradeSDK.buildVerificationInfoRequest({
      command,
      deviceType,
      mac: upgradeResponse.mac,
      aesKeys: firmware.header.aesKeys,
    });
    const responsePromise = this.sender.waitForResponse(
      {
        command,
        deviceType,
        type: ProtocolUpgradeSDK.TYPE_VERIFICATION_INFO_REQUEST,
      },
      timeout
    );
    await this.sender.sendOutputReport(reportId, packet, { deviceType });
    const response = await responsePromise;

    if (!response) {
      throw new Error(`等待 type=2 校验信息返回包超时 (${timeout}ms)`);
    }
    return ProtocolUpgradeSDK.parseVerificationInfoResponse(response);
  }

  /**
   * 从 BIN 解析结果中选择当前未运行镜像的地址、大小和 CRC。
   *
   * 设备的镜像号沿用 type=1 定义：
   * - currentImage=0：当前运行 IMG1，升级目标取 APP2_*；
   * - currentImage=1：当前运行 IMG2，升级目标取 app_*。
   *
   * @param {{currentImage:number}} upgradeResponse type=1 解析结果
   * @param {{header:Object}} firmware FirmwareBinParser 的解析结果
   * @returns {{
   *   targetImage:number,
   *   flashAddress:number,
   *   size:number,
   *   crc:number,
   *   version:number
   * }} type=6 APP 升级请求需要的目标镜像信息
   * @throws {TypeError|RangeError} 输入缺失、镜像号错误或目标镜像为空时抛出
   */
  static selectInactiveAppImage(upgradeResponse, firmware) {
    if (!upgradeResponse) {
      throw new TypeError('upgradeResponse 不能为空');
    }
    if (!firmware?.header) {
      throw new TypeError('firmware 必须是 FirmwareBinParser 的解析结果');
    }

    const currentImage = upgradeResponse.currentImage;
    ProtocolUpgradeSDK.assertIntegerInRange(
      currentImage,
      0,
      1,
      'currentImage'
    );

    const header = firmware.header;
    const target =
      currentImage === 0
        ? {
            targetImage: 1,
            flashAddress: header.APP2_start,
            size: header.APP2_size,
            crc: header.APP2_crc,
            version: header.APP2_version,
          }
        : {
            targetImage: 0,
            flashAddress: header.app_start,
            size: header.app_size,
            crc: header.app_crc,
            version: header.app_version,
          };

    ProtocolUpgradeSDK.assertIntegerInRange(
      target.flashAddress,
      0,
      0xffffffff,
      'flashAddress'
    );
    ProtocolUpgradeSDK.assertIntegerInRange(
      target.size,
      1,
      0xffffffff,
      'size'
    );
    ProtocolUpgradeSDK.assertIntegerInRange(
      target.crc,
      0,
      0xffffffff,
      'crc'
    );
    ProtocolUpgradeSDK.assertIntegerInRange(
      target.version,
      0,
      0xffff,
      'version'
    );
    return target;
  }

  /**
   * 创建 type=6 的升级 APP 请求包。
   *
   * - byte4-7: 目标镜像 Flash Address，小端 UInt32
   * - byte8-11: 目标镜像 Size，小端 UInt32
   * - byte12-15: 目标镜像 CRC，小端 UInt32
   *
   * @param {{
   *   command:number,
   *   flashAddress:number,
   *   size:number,
   *   crc:number,
   *   deviceType?:number
   * }} options
   * @returns {Uint8Array} 新创建的 64 字节 type=6 请求包
   * @throws {RangeError} 任一字段不在协议范围内时抛出
   */
  static buildAppUpgradeRequest({
    command,
    flashAddress,
    size,
    crc,
    deviceType = ProtocolUpgradeSDK.DEVICE_TYPE_DEFAULT,
  }) {
    ProtocolUpgradeSDK.assertIntegerInRange(
      flashAddress,
      0,
      0xffffffff,
      'flashAddress'
    );
    ProtocolUpgradeSDK.assertIntegerInRange(size, 1, 0xffffffff, 'size');
    ProtocolUpgradeSDK.assertIntegerInRange(crc, 0, 0xffffffff, 'crc');

    const packet = new Uint8Array(ProtocolUpgradeSDK.REPORT_SIZE);
    packet[0] = ProtocolUpgradeSDK.buildCommandByte(command, deviceType);
    packet[1] = 0;
    // type(1) + address(4) + size(4) + CRC(4) = 13 字节。
    packet[2] = 0x80 | 13;
    packet[3] = ProtocolUpgradeSDK.TYPE_APP_UPGRADE;

    const view = new DataView(packet.buffer);
    view.setUint32(4, flashAddress, true);
    view.setUint32(8, size, true);
    view.setUint32(12, crc, true);
    return packet;
  }

  /**
   * 创建 type=7 APP 校验请求包。
   *
   * - byte4: Flag，来自 type=1 返回的 dualImageModeRaw
   * - byte5-8: 使用 type=6 appRNG 计算出的 CheckData[4]
   * - byte9-12: 未运行镜像的 APP Version，小端 UInt32
   *
   * BIN 头中的 APP 版本目前是 UInt16，但协议为它保留 4 字节，因此这里
   * 按 UInt32 写入，高 2 字节自然为 0。
   *
   * @param {{
   *   command:number,
   *   flag:number,
   *   appRNG:ArrayLike<number>,
   *   appVersion:number,
   *   deviceType?:number
   * }} options
   * @returns {Uint8Array} 新创建的 64 字节 type=7 请求包
   * @throws {RangeError} Flag、appRNG 或版本字段不符合协议时抛出
   */
  static buildAppCheckRequest({
    command,
    flag,
    appRNG,
    appVersion,
    deviceType = ProtocolUpgradeSDK.DEVICE_TYPE_DEFAULT,
  }) {
    ProtocolUpgradeSDK.assertIntegerInRange(flag, 0, 0xff, 'flag');
    ProtocolUpgradeSDK.assertIntegerInRange(
      appVersion,
      0,
      0xffffffff,
      'appVersion'
    );
    const checkData = ProtocolUpgradeSDK.calculateCheckData(appRNG);

    const packet = new Uint8Array(ProtocolUpgradeSDK.REPORT_SIZE);
    packet[0] = ProtocolUpgradeSDK.buildCommandByte(command, deviceType);
    packet[1] = 0;
    // type(1) + flag(1) + CheckData(4) + APP Version(4) = 10 字节。
    packet[2] = 0x80 | 10;
    packet[3] = ProtocolUpgradeSDK.TYPE_APP_CHECK_REQUEST;
    packet[4] = flag;
    packet.set(checkData, 5);
    new DataView(packet.buffer).setUint32(9, appVersion, true);
    return packet;
  }

  /**
   * 根据当前运行镜像，选择 BIN 中需要发送的另一镜像数据。
   *
   * - currentImage=0（运行 IMG1）：发送 app2Data；
   * - currentImage=1（运行 IMG2）：发送 app1Data。
   *
   * @param {{currentImage:number}} upgradeResponse type=1 解析结果
   * @param {{app1Data:Uint8Array,app2Data:Uint8Array}} firmware BIN 解析结果
   * @returns {{targetImage:number,data:Uint8Array}} 未运行镜像及其数据
   * @throws {TypeError|RangeError} 输入缺失、镜像号错误或镜像数据为空时抛出
   */
  static selectInactiveAppData(upgradeResponse, firmware) {
    if (!upgradeResponse) {
      throw new TypeError('upgradeResponse 不能为空');
    }
    if (!firmware) {
      throw new TypeError('firmware 不能为空');
    }
    ProtocolUpgradeSDK.assertIntegerInRange(
      upgradeResponse.currentImage,
      0,
      1,
      'currentImage'
    );

    const targetImage = upgradeResponse.currentImage === 0 ? 1 : 0;
    const data = targetImage === 1 ? firmware.app2Data : firmware.app1Data;
    if (!(data instanceof Uint8Array) || data.length === 0) {
      throw new RangeError(
        `BIN 中 IMG${targetImage + 1} 的固件数据不能为空`
      );
    }
    return { targetImage, data };
  }

  /**
   * 构造一包 command=30 的固件长包。
   *
   * byte0 的 bit7 固定为 1；byte1-2 是从 0 开始的 16 位小端 Index；
   * byte3 bit0-6 是本包数据长度，bit7 仅在最后一包置 1；
   * byte4-62 最多写入 59 字节数据；byte63 由 HIDSender 写入校验和。
   *
   * @param {{
   *   index:number,
   *   data:ArrayLike<number>,
   *   isLast:boolean,
   *   deviceType?:number
   * }} options
   * @returns {Uint8Array} 新创建的 64 字节固件数据包
   * @throws {RangeError} Index、数据长度或数据字节不符合协议时抛出
   */
  static buildFirmwareDataPacket({
    index,
    data,
    isLast,
    deviceType = ProtocolUpgradeSDK.DEVICE_TYPE_DEFAULT,
  }) {
    ProtocolUpgradeSDK.assertIntegerInRange(index, 0, 0xffff, 'index');
    if (typeof isLast !== 'boolean') {
      throw new TypeError('isLast 必须是 boolean');
    }
    if (!data || data.length < 1 || data.length > ProtocolUpgradeSDK.FIRMWARE_DATA_SIZE) {
      throw new RangeError(
        `data 必须包含 1-${ProtocolUpgradeSDK.FIRMWARE_DATA_SIZE} 个字节`
      );
    }
    const payload = ProtocolUpgradeSDK.validateByteArray(
      data,
      data.length,
      'data'
    );

    const packet = new Uint8Array(ProtocolUpgradeSDK.REPORT_SIZE);
    packet[0] =
      0x80 |
      ProtocolUpgradeSDK.buildCommandByte(
        ProtocolUpgradeSDK.FIRMWARE_DATA_COMMAND,
        deviceType
      );
    new DataView(packet.buffer).setUint16(1, index, true);
    packet[3] = payload.length | (isLast ? 0x80 : 0);
    packet.set(payload, 4);
    return packet;
  }

  /**
   * 解析设备返回的 command=30 固件分包 ACK。
   *
   * ACK 没有 type 字段：
   * - byte0 bit0-4 必须为 command 30，bit5-6 是设备类型；
   * - byte1-2 是设备确认收到的 Index，小端 UInt16。
   *
   * @param {Uint8Array|ArrayBuffer} data 设备返回的 ACK 原始报告
   * @returns {{
   *   raw:Uint8Array,
   *   command:number,
   *   deviceType:number,
   *   index:number
   * }}
   * @throws {RangeError} ACK 不足 3 字节时抛出
   * @throws {Error} byte0 的 command 不是 30 时抛出
   */
  static parseFirmwareDataAck(data) {
    const response =
      data instanceof Uint8Array ? data.slice() : new Uint8Array(data || []);
    if (response.length < 3) {
      throw new RangeError('command=30 ACK 长度不能小于 3 字节');
    }

    const command = response[0] & 0x1f;
    if (command !== ProtocolUpgradeSDK.FIRMWARE_DATA_COMMAND) {
      throw new Error(
        `ACK command 错误：期望 30，实际为 ${command}`
      );
    }

    return {
      raw: response,
      command,
      deviceType: (response[0] >> 5) & 0x03,
      index: new DataView(
        response.buffer,
        response.byteOffset,
        response.byteLength
      ).getUint16(1, true),
    };
  }

  /**
   * 根据当前镜像自动选择另一镜像，发送 type=6，并等待 type=6 响应。
   *
   * 为避免设备快速返回，发送 type=6 请求前先注册响应监听。收到响应后
   * 解析并返回 appRNG，供 sendAppCheckRequest 构造 type=7 请求使用。
   * 此方法本身不执行前置的 type=2 校验。
   *
   * @param {{
   *   command:number,
   *   upgradeResponse:{currentImage:number},
   *   firmware:{header:Object},
   *   deviceType?:number,
   *   reportId?:number,
   *   timeout?:number
   * }} options
   * @returns {Promise<{target:Object, appRNGResponse:Object, sendResult:*}>}
   * @throws {Error} 发送失败、type=6 响应超时或响应格式错误时抛出
   */
  async sendAppUpgradeRequest({
    command,
    upgradeResponse,
    firmware,
    deviceType = ProtocolUpgradeSDK.DEVICE_TYPE_DEFAULT,
    reportId = 0,
    timeout = 1000,
  }) {
    ProtocolUpgradeSDK.assertIntegerInRange(reportId, 0, 0xff, 'reportId');
    ProtocolUpgradeSDK.assertIntegerInRange(timeout, 1, 60000, 'timeout');
    if (typeof this.sender.waitForResponse !== 'function') {
      throw new TypeError('sender 必须提供 waitForResponse(command, timeout) 方法');
    }
    const target = ProtocolUpgradeSDK.selectInactiveAppImage(
      upgradeResponse,
      firmware
    );
    const packet = ProtocolUpgradeSDK.buildAppUpgradeRequest({
      command,
      deviceType,
      flashAddress: target.flashAddress,
      size: target.size,
      crc: target.crc,
    });
    const responsePromise = this.sender.waitForResponse(
      {
        command,
        deviceType,
        type: ProtocolUpgradeSDK.TYPE_APP_UPGRADE,
      },
      timeout
    );
    const sendResult = await this.sender.sendOutputReport(reportId, packet, {
      deviceType,
    });
    const response = await responsePromise;
    if (!response) {
      throw new Error(`等待 type=6 APP RNG 返回包超时 (${timeout}ms)`);
    }
    const appRNGResponse = ProtocolUpgradeSDK.parseAppUpgradeResponse(response);
    this.appRNG = [...appRNGResponse.appRNG];
    return { target, appRNGResponse, sendResult };
  }

  /**
   * 使用保存的 type=6 appRNG、双镜像 Flag 和目标版本发送 type=7，
   * 然后等待并解析设备的 type=7 状态返回包。
   *
   * @param {{
   *   command:number,
   *   upgradeResponse:{dualImageModeRaw:number},
   *   target:{version:number},
   *   appRNGResponse?:{appRNG:ArrayLike<number>},
   *   deviceType?:number,
   *   reportId?:number,
   *   timeout?:number
   * }} options
   * appRNGResponse 可省略；省略时使用 SDK 实例最近保存的 this.appRNG。
   *
   * @returns {Promise<{checkData:number[], appCheckResponse:Object, sendResult:*}>}
   * @throws {Error} 发送失败、type=7 响应超时或响应格式错误时抛出
   */
  async sendAppCheckRequest({
    command,
    upgradeResponse,
    target,
    appRNGResponse,
    deviceType = ProtocolUpgradeSDK.DEVICE_TYPE_DEFAULT,
    reportId = 0,
    timeout = 1000,
  }) {
    ProtocolUpgradeSDK.assertIntegerInRange(reportId, 0, 0xff, 'reportId');
    ProtocolUpgradeSDK.assertIntegerInRange(timeout, 1, 60000, 'timeout');
    if (typeof this.sender.waitForResponse !== 'function') {
      throw new TypeError('sender 必须提供 waitForResponse(command, timeout) 方法');
    }
    if (!upgradeResponse) {
      throw new TypeError('upgradeResponse 不能为空');
    }
    if (!target) {
      throw new TypeError('target 不能为空');
    }
    const appRNG = appRNGResponse?.appRNG || this.appRNG;
    if (!appRNG) {
      throw new TypeError('尚未收到 type=6，无法取得 appRNG');
    }

    const packet = ProtocolUpgradeSDK.buildAppCheckRequest({
      command,
      deviceType,
      flag: upgradeResponse.dualImageModeRaw,
      appRNG,
      appVersion: target.version,
    });
    const responsePromise = this.sender.waitForResponse(
      {
        command,
        deviceType,
        type: ProtocolUpgradeSDK.TYPE_APP_CHECK_REQUEST,
      },
      timeout
    );
    const sendResult = await this.sender.sendOutputReport(reportId, packet, {
      deviceType,
    });
    const response = await responsePromise;
    if (!response) {
      throw new Error(`等待 type=7 APP 校验返回包超时 (${timeout}ms)`);
    }
    const appCheckResponse =
      ProtocolUpgradeSDK.parseAppCheckResponse(response);
    return {
      checkData: Array.from(packet.slice(5, 9)),
      appCheckResponse,
      sendResult,
    };
  }

  /**
   * 将未运行镜像拆成 command=30 长包并按 Index 顺序发送。
   *
   * 使用停等机制保证顺序：
   * 1. 先注册 command=30 ACK 监听，再发送当前 Index；
   * 2. 最多等待 ackTimeout（默认 100ms）；
   * 3. ACK Index 相同才发送下一包；
   * 4. 超时或 ACK Index 不同都重发当前包；
   * 5. 超过 maxRetries 后终止，避免设备断开时无限循环。
   *
   * @param {{
   *   upgradeResponse:{currentImage:number},
   *   firmware:{app1Data:Uint8Array,app2Data:Uint8Array},
   *   deviceType?:number,
   *   reportId?:number,
   *   ackTimeout?:number,
   *   maxRetries?:number,
   *   onProgress?:(progress:Object)=>void
   * }} options
   * @returns {Promise<{
   *   targetImage:number,
   *   packetCount:number,
   *   bytesSent:number,
   *   retryCount:number,
   *   totalAttempts:number
   * }>}
   * @throws {Error} 某包在重试上限内始终未收到匹配 ACK 时抛出
   */
  async sendFirmwareData({
    upgradeResponse,
    firmware,
    deviceType = ProtocolUpgradeSDK.DEVICE_TYPE_DEFAULT,
    reportId = 0,
    ackTimeout = ProtocolUpgradeSDK.FIRMWARE_ACK_TIMEOUT,
    maxRetries = ProtocolUpgradeSDK.FIRMWARE_MAX_RETRIES,
    onProgress,
  }) {
    ProtocolUpgradeSDK.assertIntegerInRange(reportId, 0, 0xff, 'reportId');
    ProtocolUpgradeSDK.assertIntegerInRange(
      ackTimeout,
      1,
      60000,
      'ackTimeout'
    );
    ProtocolUpgradeSDK.assertIntegerInRange(
      maxRetries,
      0,
      100,
      'maxRetries'
    );
    if (typeof this.sender.waitForResponse !== 'function') {
      throw new TypeError('sender 必须提供 waitForResponse(command, timeout) 方法');
    }
    if (onProgress != null && typeof onProgress !== 'function') {
      throw new TypeError('onProgress 必须是 function');
    }
    const { targetImage, data } = ProtocolUpgradeSDK.selectInactiveAppData(
      upgradeResponse,
      firmware
    );
    const packetCount = Math.ceil(
      data.length / ProtocolUpgradeSDK.FIRMWARE_DATA_SIZE
    );
    if (packetCount > 0x10000) {
      throw new RangeError(
        `固件需要 ${packetCount} 包，超出 16 位 Index 可表示的范围`
      );
    }

    // ACK 的 bit7 在不同固件实现中可能为 0 或 1。只比较 command 和
    // deviceType，避免把合法的 0x9E ACK 因完整字节不等于 0x1E 而丢弃。
    const matchFirmwareAck = (ackData) =>
      ackData.length >= 3 &&
      (ackData[0] & 0x1f) === ProtocolUpgradeSDK.FIRMWARE_DATA_COMMAND &&
      ((ackData[0] >> 5) & 0x03) === deviceType;
    let retryCount = 0;
    let totalAttempts = 0;

    for (let index = 0; index < packetCount; index++) {
      const start = index * ProtocolUpgradeSDK.FIRMWARE_DATA_SIZE;
      const end = Math.min(
        start + ProtocolUpgradeSDK.FIRMWARE_DATA_SIZE,
        data.length
      );
      const packet = ProtocolUpgradeSDK.buildFirmwareDataPacket({
        index,
        data: data.subarray(start, end),
        isLast: index === packetCount - 1,
        deviceType,
      });

      let acknowledged = false;
      let lastReason = '';
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        const attemptNumber = attempt + 1;
        // HIDSender 会在发送副本中写入 byte63 校验和。这里制作相同的
        // 日志副本并提前计算校验和，让控制台展示实际发送的完整 64 字节。
        const loggedPacket = packet.slice();
        let loggedPacketSum = 0;
        for (let checksumIndex = 0; checksumIndex < 63; checksumIndex++) {
          loggedPacketSum += loggedPacket[checksumIndex];
        }
        loggedPacket[63] =
          (0xa1 - (loggedPacketSum & 0xff)) & 0xff;
        const packetHex = Array.from(loggedPacket)
          .map((byte) => byte.toString(16).padStart(2, '0').toUpperCase())
          .join(' ');
        const packetFields = {
          byte0: `0x${packet[0].toString(16).padStart(2, '0').toUpperCase()}`,
          command: packet[0] & 0x1f,
          deviceType: (packet[0] >> 5) & 0x03,
          longPacketMode: (packet[0] & 0x80) !== 0,
          index: packet[1] | (packet[2] << 8),
          dataLength: packet[3] & 0x7f,
          isLast: (packet[3] & 0x80) !== 0,
          checksum: `0x${loggedPacket[63]
            .toString(16)
            .padStart(2, '0')
            .toUpperCase()}`,
        };
        // 使用 warn，确保 DevTools 只显示 Warning/Error 时仍然可见。
        console.warn(
          `[command=30 REQUEST] Index=${index} Attempt=${attemptNumber}/${
            maxRetries + 1
          }\nFields=${JSON.stringify(packetFields)}\nHEX=${packetHex}`
        );
        console.warn('[command=30 REQUEST BYTES]', Array.from(loggedPacket));

        // 必须先监听再发送，避免设备同步快速返回 ACK 时丢包。
        const ackPromise = this.sender.waitForResponse(
          matchFirmwareAck,
          ackTimeout
        );
        await this.sender.sendOutputReport(reportId, packet, { deviceType });
        totalAttempts += 1;

        const ackRaw = await ackPromise;
        if (!ackRaw) {
          lastReason = `${ackTimeout}ms 内未收到 ACK`;
          console.warn('[command=30 TIMEOUT]', {
            expectedIndex: index,
            timeout: ackTimeout,
            nextAction:
              attempt < maxRetries ? '重发当前分包' : '超过重试上限',
          });
        } else {
          const ack = ProtocolUpgradeSDK.parseFirmwareDataAck(ackRaw);
          const ackHex = Array.from(ackRaw)
            .map((byte) =>
              byte.toString(16).padStart(2, '0').toUpperCase()
            )
            .join(' ');
          console.warn(
            `[command=30 ACK] ExpectedIndex=${index} ReceivedIndex=${ack.index}\nHEX=${ackHex}`
          );
          console.warn('[command=30 ACK BYTES]', Array.from(ackRaw));
          if (ack.index === index) {
            acknowledged = true;
            console.warn(`[command=30 ACK OK] Index=${index}`);
            break;
          }
          lastReason = `ACK Index=${ack.index}，期望 Index=${index}`;
          console.warn('[command=30 ACK INDEX MISMATCH]', {
            expectedIndex: index,
            receivedIndex: ack.index,
            nextAction:
              attempt < maxRetries ? '重发当前分包' : '超过重试上限',
          });
        }

        if (attempt < maxRetries) {
          retryCount += 1;
        }
      }

      if (!acknowledged) {
        throw new Error(
          `固件分包 Index=${index} 发送失败：${lastReason}，已重试 ${maxRetries} 次`
        );
      }

      onProgress?.({
        index,
        packetCount,
        acknowledgedPackets: index + 1,
        bytesSent: Math.min(
          (index + 1) * ProtocolUpgradeSDK.FIRMWARE_DATA_SIZE,
          data.length
        ),
        totalBytes: data.length,
        retryCount,
      });
    }

    return {
      targetImage,
      packetCount,
      bytesSent: data.length,
      retryCount,
      totalAttempts,
    };
  }

  /**
   * 执行“type=2 → type=6 → command=30 固件数据 → type=7”的连续流程。
   *
   * type=2 的 byte4 非 0 时立即停止，不会发送 type=6。这让调用方可以
   * 检查 verificationResponse.status。固件所有分包收到匹配 ACK 后才
   * 发送 type=7；type=7 的 byte4 状态作为本轮更新的最终校验结果。
   *
   * @param {Parameters<ProtocolUpgradeSDK['sendVerificationInfoRequest']>[0]} options
   * @returns {Promise<{
   *   verificationResponse:Object,
   *   appUpgradeSent:boolean,
   *   appCheckSent:boolean,
   *   firmwareDataSent:boolean,
   *   target:Object|null,
   *   appRNGResponse:Object|null,
   *   appCheckResponse:Object|null,
   *   checkData:number[]|null,
   *   firmwareResult:Object|null,
   *   sendResult:*|null
   * }>}
   */
  async sendVerificationAndStartAppUpgrade(options) {
    const verificationResponse =
      await this.sendVerificationInfoRequest(options);

    if (!verificationResponse.success) {
      return {
        verificationResponse,
        appUpgradeSent: false,
        appCheckSent: false,
        firmwareDataSent: false,
        target: null,
        appRNGResponse: null,
        appCheckResponse: null,
        checkData: null,
        firmwareResult: null,
        sendResult: null,
      };
    }

    const { target, appRNGResponse } =
      await this.sendAppUpgradeRequest(options);
    const firmwareResult = await this.sendFirmwareData(options);
    const { checkData, appCheckResponse, sendResult } =
      await this.sendAppCheckRequest({
      ...options,
      target,
      appRNGResponse,
    });
    if (!appCheckResponse.success) {
      return {
        verificationResponse,
        appUpgradeSent: true,
        appCheckSent: true,
        firmwareDataSent: true,
        target,
        appRNGResponse,
        appCheckResponse,
        checkData,
        firmwareResult,
        sendResult,
      };
    }

    return {
      verificationResponse,
      appUpgradeSent: true,
      appCheckSent: true,
      firmwareDataSent: true,
      target,
      appRNGResponse,
      appCheckResponse,
      checkData,
      firmwareResult,
      sendResult,
    };
  }

  /**
   * 发送 type=1 的升级请求并等待 type=1 返回包。
   *
   * 调用顺序是“先注册响应监听，再发送请求”，以免设备立即返回导致
   * 浏览器漏收。成功后直接返回 parseUpgradeRequestResponse 的结果，
   * 可继续传给 sendVerificationInfoRequest。
   *
   * @param {{
   *   command:number,
   *   productId:number,
   *   deviceType?:number,
   *   reportId?:number,
   *   timeout?:number
   * }} options
   * @param {number} options.command 升级协议使用的 5-bit 命令号
   * @param {number} options.productId 请求升级的产品 ID
   * @param {number} [options.deviceType=0] 设备类型
   * @param {number} [options.reportId=0] WebHID reportId；通常保持 0
   * @param {number} [options.timeout=1000] 等待设备返回的毫秒数
   * @returns {Promise<ReturnType<typeof ProtocolUpgradeSDK.parseUpgradeRequestResponse>>}
   *   已解析的 type=1 返回信息
   * @throws {Error} 发送失败、响应超时或返回包格式错误时抛出
   */
  async sendUpgradeRequest({
    command,
    productId,
    deviceType = ProtocolUpgradeSDK.DEVICE_TYPE_DEFAULT,
    reportId = 0,
    timeout = 1000,
  }) {
    ProtocolUpgradeSDK.assertIntegerInRange(reportId, 0, 0xff, 'reportId');
    ProtocolUpgradeSDK.assertIntegerInRange(timeout, 1, 60000, 'timeout');
    if (typeof this.sender.waitForResponse !== 'function') {
      throw new TypeError('sender 必须提供 waitForResponse(command, timeout) 方法');
    }

    const packet = ProtocolUpgradeSDK.buildUpgradeRequest({
      command,
      productId,
      deviceType,
    });
    // 新会话不能复用上一轮 type=6 的随机数据。
    this.appRNG = null;
    // 先监听再发送，避免设备立即返回时丢失响应。
    const responsePromise = this.sender.waitForResponse(
      {
        command,
        deviceType,
        type: ProtocolUpgradeSDK.TYPE_UPGRADE_REQUEST,
      },
      timeout
    );
    await this.sender.sendOutputReport(reportId, packet, { deviceType });
    const response = await responsePromise;

    if (!response) {
      throw new Error(`等待 type=1 升级请求返回包超时 (${timeout}ms)`);
    }
    return ProtocolUpgradeSDK.parseUpgradeRequestResponse(response);
  }

  /**
   * 内部通用数值校验：确保协议字段是指定范围内的整数。
   *
   * JavaScript 的位运算会静默截断小数或过大的数。构包前显式校验，
   * 可以尽早指出具体字段，而不是发送一个已经被隐式截断的错误数据包。
   *
   * @param {*} value 要检查的字段值
   * @param {number} min 允许的最小值，包含边界
   * @param {number} max 允许的最大值，包含边界
   * @param {string} name 错误信息中显示的字段名
   * @returns {void}
   * @throws {RangeError} value 不是整数或不在范围内时抛出
   */
  static assertIntegerInRange(value, min, max, name) {
    if (!Number.isInteger(value) || value < min || value > max) {
      throw new RangeError(`${name} 必须是 ${min}-${max} 范围内的整数`);
    }
  }

  /**
   * 内部字节数组校验，并转换成可安全写入协议包的 Uint8Array。
   *
   * 用途：
   * 1. 检查数组长度是否与协议固定字段一致，例如 MAC 必须是 5 字节；
   * 2. 检查每一项是否为 0-255 的整数；
   * 3. 校验通过后复制为 Uint8Array，供 packet.set() 写入。
   *
   * 不能直接使用 Uint8Array.from(value) 代替校验，因为它会静默处理错误
   * 数据，例如 256 会变成 0、-1 会变成 255、小数会被截断。
   *
   * @param {ArrayLike<number>} value 待检查的普通数组或 Uint8Array
   * @param {number} expectedLength 协议规定的精确字节数
   * @param {string} name 错误信息中显示的字段名，例如 mac、aesKeys
   * @returns {Uint8Array} 校验通过后的独立字节数组副本
   * @throws {RangeError} 长度不符或任一元素不是有效字节时抛出
   */
  static validateByteArray(value, expectedLength, name) {
    if (
      !value ||
      typeof value.length !== 'number' ||
      value.length !== expectedLength
    ) {
      throw new RangeError(`${name} 必须包含 ${expectedLength} 个字节`);
    }

    for (let index = 0; index < value.length; index++) {
      ProtocolUpgradeSDK.assertIntegerInRange(
        value[index],
        0,
        0xff,
        `${name}[${index}]`
      );
    }
    return Uint8Array.from(value);
  }
}

export default ProtocolUpgradeSDK;
